import React, { useState } from "react";

import { Button as AntButton } from "antd";

import { FormulaEditor } from "shared/components/vega/ExpressionEngine";
import { useActiveUser } from "shared/hooks/api";

interface V1MigrationPreviewProps {
  originalFormula: string;
  previewFormula: string;
  canEdit: boolean;
  canSave: boolean;
  isEdited: boolean;
  onChange: (formula: string) => void;
  onReset: () => void;
  onSave: () => void;
  onDelete: () => void;
  onRestorePreview: () => void;
}

interface PanelProps {
  title: React.ReactNode;
  headerAction?: React.ReactNode;
  background: string;
  borderColor: string;
  children: React.ReactNode;
}

const Panel: React.FC<PanelProps> = ({
  title,
  headerAction,
  background,
  borderColor,
  children,
}) => (
  <div style={{ flex: 1, minWidth: 0 }}>
    <div
      style={{
        padding: "8px 12px",
        fontWeight: 600,
        fontSize: 13,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        background,
        borderBottom: `1px solid ${borderColor}`,
      }}
    >
      <span>{title}</span>
      {headerAction}
    </div>
    {children}
  </div>
);

/**
 * Side-by-side comparison view for V1 expression migration.
 * Shows the legacy formula (read-only) next to the V1 preview (editable).
 */
const V1MigrationPreview: React.FC<V1MigrationPreviewProps> = ({
  originalFormula,
  previewFormula,
  canEdit,
  canSave,
  isEdited,
  onChange,
  onReset,
  onSave,
  onDelete,
  onRestorePreview,
}) => {
  const { activeUser } = useActiveUser();
  // Incrementing the key forces the editor to remount, clearing any error state
  const [editorKey, setEditorKey] = useState(0);

  const isVeroEmployee = activeUser?.email?.endsWith("@sayvero.com");

  const handleRestorePreview = () => {
    setEditorKey((prev) => prev + 1);
    onRestorePreview();
  };

  if (!isVeroEmployee) {
    return null;
  }

  return (
    <div className="vega applicant-workflow workflow-editor v1-migration-preview">
      <div style={{ display: "flex", gap: 16, width: "100%" }}>
        <Panel title="Current (Legacy)" background="#f5f5f5" borderColor="#e0e0e0">
          <FormulaEditor
            formula={originalFormula}
            canSave={false}
            canEdit={false}
            onChange={() => {}}
            readOnlyFormat
          />
        </Panel>
        <Panel
          title={<>V1 Preview {isEdited && "(Edited)"}</>}
          headerAction={
            isEdited && (
              <AntButton size="small" onClick={handleRestorePreview}>
                Restore Preview
              </AntButton>
            )
          }
          background={isEdited ? "#fff7e6" : "#e6f7e6"}
          borderColor={isEdited ? "#ffd591" : "#b7eb8f"}
        >
          <FormulaEditor
            key={editorKey}
            formula={previewFormula}
            canSave={canSave}
            canEdit={canEdit}
            onChange={onChange}
            onReset={onReset}
            onSave={onSave}
            onDelete={onDelete}
            readOnlyFormat={false}
          />
        </Panel>
      </div>
    </div>
  );
};

export default V1MigrationPreview;
